import { Component, OnInit, OnChanges, SimpleChanges, signal, inject, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormControl } from '@angular/forms';
import { ProfessionalsService } from '../../core/services/professionals.service'; 
import { 
  ProfessionalPaymentRateConfig, 
  ProfessionalContractPaymentRateAnswer,
  ProfessionalContractPaymentRatesWrapper,
  PaymentRatesSectionStatus 
} from '../../core/models/professional.models';
import { finalize } from 'rxjs/operators';

// PrimeNG 2026
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Button } from 'primeng/button';
import { ProgressSpinner } from 'primeng/progressspinner';
import { Toast } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

@Component({
  selector: 'app-payment-rate',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Select, InputText, Button, ProgressSpinner, Toast, TooltipModule],
  providers: [MessageService],
  templateUrl: './payment-rate.html',
  styleUrl: './payment-rate.scss'
})
export class PaymentRateComponent implements OnInit, OnChanges {
  private fb = inject(FormBuilder);
  private profService = inject(ProfessionalsService);
  private messageService = inject(MessageService);
  private titleService = inject(Title);

  @Input() professionalType: string = 'AUTO_EXPERT'; 
  @Input() contractId: string = ''; 
  @Output() statusChanged = new EventEmitter<PaymentRatesSectionStatus>();
  @Output() saveAndProceed = new EventEmitter<void>();

  form: FormGroup = this.fb.group({});
  paymentRates = signal<ProfessionalPaymentRateConfig[]>([]);
  isLoading = signal(false);
  isSubmitting = signal(false);
  sectionStatus = signal<PaymentRatesSectionStatus>(null);
  private loadedConfigurationKey: string | null = null;

  ngOnInit() {
    this.titleService.setTitle('Tariffe di Pagamento - Portale Fiduciari AXA');
    this.loadRatesIfReady();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['professionalType'] || changes['contractId']) {
      this.loadRatesIfReady();
    }
  }

  private loadRatesIfReady(): void {
    if (this.professionalType && this.contractId) {
      const configurationKey = `${this.professionalType}:${this.contractId}`;
      if (configurationKey === this.loadedConfigurationKey) {
        return;
      }

      this.loadedConfigurationKey = configurationKey;
      this.loadRatesConfiguration();
    }
  }

  loadRatesConfiguration() {
    this.isLoading.set(true);
    this.profService.getPaymentRatesByProfessionalType(this.professionalType)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.paymentRates.set(res.data);
            this.buildForm(res.data);
            // Dopo aver costruito il form, carica i dati salvati
            this.loadSavedRates();
          }
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Errore nel recupero configurazione' })
      });
  }

  loadSavedRates() {
    this.profService.getProfessionalContractPaymentRates(this.contractId)
      .subscribe({
        next: (res) => {
          if (res.status && res.data) {
            this.sectionStatus.set(res.data.sectionStatus);
            this.statusChanged.emit(res.data.sectionStatus);

            if (res.data.paymentRates.length === 0) {
              this.prefillDefaultRates();
            } else {
              this.populateForm(res.data.paymentRates);
            }
          }
        },
        error: () => {
          // Se non ci sono dati salvati, è la prima volta - non mostrare errore
          this.sectionStatus.set(null);
          this.statusChanged.emit(null);
          this.prefillDefaultRates();
        }
      });
  }

  private buildForm(data: ProfessionalPaymentRateConfig[]) {
    this.form = this.fb.group({});
    data.forEach(rate => {
      this.form.addControl(rate.fieldId, new FormControl('', rate.isMandatory ? Validators.required : null));
      // Il controllo _OTHER è usato solo dai DROPDOWN con opzione OTHER
      if (rate.prType !== 'INPUT_TEXT') {
        this.form.addControl(
          `${rate.fieldId}_OTHER`,
          new FormControl(null, Validators.maxLength(255)),
        );
      }
    });
  }

  // Override del default per i campi multi-valore dove il valore da preselezionare (requisito cliente)
  // NON è la prima opzione non-OTHER. Chiave = fieldId, valore = valueKey da preselezionare.
  private readonly defaultRateOverrides: Record<string, string> = {
    DOPR_002: '250.00', // Ctu: default 250,00 (non 230,00)
    DOPR_003: '250.00', // Arbitrato: default 250,00 (non 230,00)
    REPR_010: '160.00', // RE-M.C. Fenomeno Elettrico: default 160,00 con tecnico (non 85,00)
  };

  // Pre-popola ogni dropdown con il valore di default: override esplicito se presente e disponibile,
  // altrimenti la prima opzione non-OTHER. Vale per tutti i tipi perito; gli INPUT_TEXT restano vuoti.
  private prefillDefaultRates(): void {
    this.paymentRates().forEach(rate => {
      const options = rate.dropdownOptions;
      if (!options?.length) {
        return;
      }

      const overrideKey = this.defaultRateOverrides[rate.fieldId];
      const overrideOption = overrideKey
        ? options.find(option => option.valueKey === overrideKey)
        : undefined;
      const defaultOption =
        overrideOption ?? options.find(option => option.valueKey.toUpperCase() !== 'OTHER');

      if (defaultOption && !this.form.get(rate.fieldId)?.value) {
        this.form.get(rate.fieldId)?.setValue(defaultOption.valueKey);
      }
    });
  }

  private populateForm(savedRates: ProfessionalContractPaymentRateAnswer[]) {
    savedRates.forEach(savedRate => {
      // Trova il campo corrispondente usando l'id
      const rateConfig = this.paymentRates().find(r => r.id === savedRate.paymentRateId);
      if (rateConfig) {
        this.form.get(rateConfig.fieldId)?.setValue(savedRate.value);
        
        // Se è OTHER, popola anche il campo custom
        if (savedRate.value === 'OTHER' && savedRate.customAnswer) {
          this.form.get(`${rateConfig.fieldId}_OTHER`)?.setValue(savedRate.customAnswer);
        }
        if (savedRate.value === 'OTHER') {
          this.updateCustomAnswerValidation(rateConfig.fieldId);
        }
      }
    });
  }

  isOtherSelected(fieldId: string): boolean {
    const rate = this.paymentRates().find(r => r.fieldId === fieldId);
    if (!rate || rate.prType === 'INPUT_TEXT') return false;
    return this.form.get(fieldId)?.value === 'OTHER';
  }

  onRateValueChange(fieldId: string): void {
    this.updateCustomAnswerValidation(fieldId);
  }

  private updateCustomAnswerValidation(fieldId: string): void {
    const rate = this.paymentRates().find(item => item.fieldId === fieldId);
    const customAnswerControl = this.form.get(`${fieldId}_OTHER`);
    if (!rate || !customAnswerControl) {
      return;
    }

    const validators = [Validators.maxLength(255)];
    if (rate.isMandatory && this.isOtherSelected(fieldId)) {
      validators.push(Validators.required);
    }

    customAnswerControl.setValidators(validators);
    customAnswerControl.updateValueAndValidity({ emitEvent: false });
  }

  getControl(id: string): FormControl {
    return this.form.get(id) as FormControl;
  }

  saveDraft() {
    this.saveRates('DRAFT');
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Compila tutti i campi obbligatori prima di proseguire'
      });
      return;
    }
    this.saveRates('FILLED');
  }

  private saveRates(status: 'DRAFT' | 'FILLED') {
    this.isSubmitting.set(true);

    const answers: ProfessionalContractPaymentRateAnswer[] = this.paymentRates()
      .map(rate => {
        const selectedValue = this.form.get(rate.fieldId)?.value;
        const value = selectedValue === null || selectedValue === undefined ? '' : String(selectedValue);

        if (!value.trim()) {
          return null;
        }

        let customAnswer: string | null = null;

        if (value === 'OTHER') {
          const rawTextValue = this.form.get(`${rate.fieldId}_OTHER`)?.value;
          if (rawTextValue !== null && rawTextValue !== undefined) {
            const textValue = String(rawTextValue).trim();
            customAnswer = textValue || null;
          }
        }

        return {
          paymentRateId: rate.id,
          value,
          customAnswer
        };
      })
      .filter((answer): answer is ProfessionalContractPaymentRateAnswer => answer !== null);

    const payload: ProfessionalContractPaymentRatesWrapper = {
      paymentRates: answers,
      sectionStatus: status
    };

    this.profService.putProfessionalContractPaymentRates(this.contractId, payload)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe({
        next: () => {
          this.sectionStatus.set(status);
          this.statusChanged.emit(status);
          const message = status === 'DRAFT' ? 'Bozza salvata' : 'Tariffe salvate';
          this.messageService.add({ severity: 'success', summary: 'Salvato', detail: message });
          
          // Se è FILLED, emetti evento per proseguire allo step successivo
          if (status === 'FILLED') {
            this.saveAndProceed.emit();
          }
        },
        error: () => this.messageService.add({ severity: 'error', summary: 'Errore', detail: 'Salvataggio fallito' })
      });
  }
}
